'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var useScale = require('./use-scale.js');
var useTranslateXy = require('./use-translate-xy.js');

/**
 * React hook for panning and zooming a child within a container.
 * Combines `useTranslateXY` and `useScale` into one React hook.
 * Automatically applies CSS `scale(1);` and `translate: (0px, 0px);` to the child element.
 *
 * @params UsePanZoomOptions
 * @returns UsePanZoomState
 */ const usePanZoom = (options)=>{
    const { childRef , containerRef , enableKeyboardNudge , enableMouseWheel , enableTouch , enableTrackpad , enabled =true , nudgeIncrement , padding , resetOnDisable =false , zoomInMax , zoomInSteps , zoomOutMax , zoomOutSteps  } = options;
    const enabledPan = typeof enabled === 'boolean' ? enabled : enabled.pan || enabled.pan === undefined;
    const enabledZoom = typeof enabled === 'boolean' ? enabled : enabled.zoom || enabled.zoom === undefined;
    const enabledPanOrZoom = enabledPan || enabledZoom;
    const { reset: resetZoom , scale: zoom , scaleIn: zoomIn , scaleOut: zoomOut , scaled: zoomed , scaledInMax: zoomedInMax , scaledOutMax: zoomedOutMax , scaling: zooming  } = useScale.useScale({
        enableMouseWheel,
        enableTouch,
        enableTrackpad,
        enabled: enabledZoom,
        ref: containerRef,
        scaleInMax: zoomInMax,
        scaleInSteps: zoomInSteps,
        scaleOutMax: zoomOutMax,
        scaleOutSteps: zoomOutSteps
    });
    const { reset: resetPan , setTranslate , translate: pan , translated: panned , translating: panning  } = useTranslateXy.useTranslateXY({
        childRef,
        containerRef,
        enableKeyboardNudge,
        enabled: enabledPan,
        nudgeIncrement,
        padding
    });
    const reset = React.useCallback(()=>{
        resetPan();
        resetZoom();
    }, [
        resetPan,
        resetZoom
    ]);
    React.useEffect(()=>{
        const child = childRef?.current;
        if (!child) return;
        if ((!enabledPan || !enabledZoom) && resetOnDisable) {
            reset();
            child.style.removeProperty('scale');
            child.style.removeProperty('translate');
            child.style.removeProperty('will-change');
        }
    }, [
        childRef,
        enabledPan,
        enabledZoom,
        resetOnDisable
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    React.useEffect(()=>{
        const child = childRef?.current;
        if (!enabledPanOrZoom || !child) return;
        child.style.scale = `${zoom}`;
        /**
     * NOTE: Math.round prevents blurriness
     */ child.style.translate = `${Math.round(pan.x)}px ${Math.round(pan.y)}px`;
        child.style.willChange = 'scale, translate';
    }, [
        childRef,
        enabledPanOrZoom,
        pan,
        zoom
    ]);
    const transitionEnd = (e)=>{
        if (!childRef?.current) return;
        if (e.propertyName === 'translate') childRef.current.style.removeProperty('transition');
    };
    const panToElement = (ref, transitionDuration = 200, transitionTimingFunction = 'ease')=>{
        const child = childRef?.current;
        const container = containerRef?.current;
        const element = ref.current;
        if (!child || !container || !element) return;
        const childRect = child.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const childOffsetLeft = child.offsetLeft < 0 ? child.offsetLeft * zoom : 0;
        const x = childOffsetLeft + (childRect.width - containerRect.width) / 2 + containerRect.width / 2 - element.offsetLeft * zoom - elementRect.width / 2;
        const childoffsetTop = child.offsetTop < 0 ? child.offsetTop * zoom : 0;
        const y = childoffsetTop + (childRect.height - containerRect.height) / 2 + containerRect.height / 2 - element.offsetTop * zoom - elementRect.height / 2;
        child.style.transition = `translate ${transitionDuration}ms ${transitionTimingFunction}`;
        child.addEventListener('transitionend', transitionEnd);
        setTranslate({
            x,
            y
        });
    };
    return {
        pan,
        panToElement,
        panned,
        panning,
        reset,
        resetPan,
        resetZoom,
        zoom,
        zoomIn,
        zoomedInMax,
        zoomOut,
        zoomedOutMax,
        zoomed,
        zooming
    };
};

exports.usePanZoom = usePanZoom;
//# sourceMappingURL=use-pan-zoom.js.map
